import CalendarFolderTypeEnum from 'owa-service/lib/contract/CalendarFolderTypeEnum';
import GetCalendarFolderConfigurationResponse from 'owa-service/lib/contract/GetCalendarFolderConfigurationResponse';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import LocalCacheForRemoteCalendarEntry from 'owa-service/lib/contract/LocalCacheForRemoteCalendarEntry';
import { createExplicitLogonRequest } from 'owa-headers';
import { getCalendarFolderConfigurationService } from '../services/getCalendarFolderConfigurationService';
import folderId from 'owa-service/lib/factory/folderId';
import {
    addToCalendarsCache,
    markCalendarEntryAsValid,
    calendarUpdated,
    getFolderIdByCalendarID,
    getCalculatedFolderIdByCalendarID,
    getCalendarIdByFolderId,
    getCalendarEntryByCalendarId,
    removeCalendarWithIDFromCalendarsCache,
} from 'owa-calendar-cache';
import { MailboxInfo } from 'owa-client-ids';
import { getUserMailboxInfo, getRoutingKeyPrefixForSmtpAddress } from 'owa-client-ids';
import { logUsage } from 'owa-analytics';
import { calendarFolderIdUpdated } from '../actions/publicActions';
import { setCalendarLoadState } from '../actions/internalActions';

/**
 * Fetches the actual calendar folder id for a calendar and updates the the calendar
 * cache with the actual calendar folder id.
 *
 * **Why is this needed?**
 * When we fetch calendars and put them in the cache via initializeCalendarCache,
 * some of calendars have invalid folder ids. These should be updated as need by consumers of the cache with this function.
 *
 * **When is this needed?**
 * The calendar folder id needs to be updated when we want to fetch events for a calendar or look up a calendar
 * by the calendar folder property associated with an event.
 *
 * **Why isn't this taken care of during initialization?**
 * Because extra requests/ processing are needed to obtain an updated calendar folder id, so we do not want to do this
 * extra work until the data is needed by a consumer of the cache. This is why it is up to the consumer of the
 * cache update the calendar folder id as needed.
 *
 * @param calendarId the calendar id which needs its calendar folder id updated
 */
export async function getAndUpdateActualFolderId(calendarId: string) {
    try {
        setCalendarLoadState(calendarId, 'Loading');
        await getAndUpdateActualFolderIdInternal(calendarId);
    } catch (error) {
        // if response code corresponds to ErrorTooManyObjectsOpened or ErrorApiQuarantined, then mark loadingState as "Throttled"
        if (
            error.message &&
            (error.message.indexOf('ErrorApiQuarantined') >= 0 ||
                error.message.indexOf('ErrorTooManyObjectsOpened') >= 0)
        ) {
            setCalendarLoadState(calendarId, 'Throttled');
        } else {
            setCalendarLoadState(calendarId, 'Failed');
        }
    }
}

export async function getAndUpdateActualFolderIdInternal(calendarId: string) {
    const calendarEntry: LocalCacheForRemoteCalendarEntry = getCalendarEntryByCalendarId(
        calendarId
    ) as LocalCacheForRemoteCalendarEntry;
    if (!calendarEntry) {
        throw new Error(`Unable to get calendar entry for calendar ${calendarId}`);
    }

    const calendarFolderId = getFolderIdByCalendarID(calendarId);
    const calculatedFolderId = getCalculatedFolderIdByCalendarID(calendarId);
    const fullCalendarId = getCalendarIdByFolderId(calendarFolderId);
    const mailboxInfo = fullCalendarId ? fullCalendarId.mailboxInfo : getUserMailboxInfo();

    let email;
    let getCalendarFolderConfigResponse;
    if (
        calendarEntry.CalendarFolderType == CalendarFolderTypeEnum.PublicCalendarFolder &&
        getUserConfiguration().SessionSettings.DefaultPublicFolderMailbox
    ) {
        email = getUserConfiguration().SessionSettings.DefaultPublicFolderMailbox;
    } else {
        email = getEmailAddress(calendarEntry.OwnerEmailAddress, mailboxInfo);
    }

    try {
        getCalendarFolderConfigResponse = await getCalendarFolderConfigurationService(
            folderId({
                Id: calculatedFolderId,
            }),
            email
        );
    } catch (error) {
        // There are cases wherein GetCalendarFolderConfigurationService seems to return 200's with mailboxNotFound messages
        // and in certain scenarios 500s with a redirect. In certain hybrid topologies, when online mailboxes are trying to access
        // a shared calendar from on-prem, a 500 is returned and a 200 with error code 31 on other lookup failures.
        // Either ways, the current behavior is to fall back to making a call to GetUserAvailability and fetch f/b data for this calendar.
        logUsage('getCalendarFolderConfigurationServiceFailed');
        markCalendarEntryAsValid(calendarEntry);
        return;
    }

    if (getCalendarFolderConfigResponse) {
        // We need to mark CalendarEntry as valid even when we don't have a FolderId to support previous
        // calendar sharing model that uses LinkedCalendarEntries. We won't have a FolderId in scenarios
        // where the user doesn't have permissions on the calendar and can only get FreeBusy information
        markCalendarEntryAsValid(calendarEntry);
        if (calendarEntry.CalendarFolderType == CalendarFolderTypeEnum.PublicCalendarFolder) {
            addPublicFolderCalendarsToStore(
                getCalendarFolderConfigResponse,
                calendarEntry,
                mailboxInfo
            );
        } else {
            updateStoreWithActualFolderId(
                getCalendarFolderConfigResponse,
                calendarEntry,
                mailboxInfo,
                calendarId
            );
        }
        calendarFolderIdUpdated();
    } else {
        throw new Error(`Unable to get folder for calendar ${calendarId}`);
    }
}

function addPublicFolderCalendarsToStore(
    getCalendarFolderConfigResponse: GetCalendarFolderConfigurationResponse,
    calendarEntry: LocalCacheForRemoteCalendarEntry,
    mailboxInfo: MailboxInfo
) {
    let calendarFolder = getCalendarFolderConfigResponse.CalendarFolder;
    if (calendarFolder) {
        if (!calendarEntry.CalendarFolderId) {
            calendarEntry.CalendarFolderId =
                getCalendarFolderConfigResponse.CalendarFolder.FolderId;
        }
        const PFmailboxId = calendarFolder.ReplicaList[0];
        addToCalendarsCache(calendarEntry, calendarFolder, {
            type: 0,
            deprecatedMailboxId: PFmailboxId,
            primarySmtpAddress: PFmailboxId,
            routingKey: getRoutingKeyPrefixForSmtpAddress() + PFmailboxId,
            requestOptions: createExplicitLogonRequest(PFmailboxId, mailboxInfo),
            returnModernConversationItems: mailboxInfo.returnModernConversationItems,
        });
    }
}

function updateStoreWithActualFolderId(
    getCalendarFolderConfigResponse: GetCalendarFolderConfigurationResponse,
    calendarEntry: LocalCacheForRemoteCalendarEntry,
    mailboxInfo: MailboxInfo,
    calendarId: string
) {
    let calendarFolder = getCalendarFolderConfigResponse.CalendarFolder;
    if (calendarFolder?.EffectiveRights?.Read) {
        if (getCalendarFolderConfigResponse.MasterList) {
            calendarEntry.RemoteCategories = getCalendarFolderConfigResponse.MasterList;
        }

        if (
            calendarId == calendarEntry.OwnerEmailAddress ||
            (calendarEntry.ItemId && calendarId == calendarEntry.ItemId.Id)
        ) {
            calendarUpdated(calendarId, {
                id: {
                    id: calendarEntry.ItemId.Id,
                    mailboxInfo: mailboxInfo,
                },
                calculatedFolderId: calendarFolder.FolderId.Id,
            });

            removeCalendarWithIDFromCalendarsCache(calendarId);

            // now we add the actual calendarFolder to the cache
            addToCalendarsCache(calendarEntry, calendarFolder, mailboxInfo);
        }
    }
}

function getEmailAddress(ownerEmailAddress: string, mailboxInfo: MailboxInfo) {
    if (ownerEmailAddress) {
        return ownerEmailAddress;
    } else if (mailboxInfo) {
        return mailboxInfo.primarySmtpAddress;
    } else {
        return null;
    }
}
